import type { KeyboardEventHandler, ReactNode } from 'react';
import { useMemo } from 'react';
import type { FormatOptionLabelMeta, GroupBase, StylesConfig } from 'react-select';
import SelectAsync from 'react-select';
import { useQuery } from '@tanstack/react-query';
import type { SmartRoutingEntityType, SmartRoutingSearchEntity } from '@trinity/chat';
import {
  buildSelectStyles,
  buildSelectTheme,
} from '@trinity/chat-components/components/Chat.Filters/Presentation.js';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useSelectedUserTerritoryId } from '../Chat.Filters/hooks/useSelectedUserTerritoryId';

type SelectEntitiesProps = {
  disabled?: boolean;
  formatOptionLabel?: (
    data: SmartRoutingSearchEntity,
    formatOptionLabelMeta: FormatOptionLabelMeta<SmartRoutingSearchEntity>
  ) => ReactNode;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: (value: string) => void;
  onSelection: (result: SmartRoutingSearchEntity | null) => void;
  placeholder?: string;
  inputValue?: string;
  isClearable?: boolean;
  value?: SmartRoutingSearchEntity | null;
  validEntities: SmartRoutingEntityType[];
  queryFn: (params: {
    name: string;
    brandIdentifier: string;
    territoryId?: string;
    validEntities: SmartRoutingEntityType[];
  }) => Promise<{ results: SmartRoutingSearchEntity[] }>;
};

function defaultGetOptionLabel(option: SmartRoutingSearchEntity): string {
  return option.name;
}

function defaultGetOptionValue(option: SmartRoutingSearchEntity): string {
  return `${option.entityType}:${option.entityId.toString()}`;
}

function defaultFormatOptionLabel(result: SmartRoutingSearchEntity): ReactNode {
  const locationAddress = result.location.address;

  if (result.entityType === 'account') {
    return (
      <div>
        <div>{result.name}</div>
        {result.category ? <div>{result.category}</div> : null}
        {locationAddress ? <div>{locationAddress}</div> : null}
      </div>
    );
  }

  const specialtyLabel = result.specialty?.join(', ');

  return (
    <div>
      <div>{result.name}</div>
      {specialtyLabel ? <div>{specialtyLabel}</div> : null}
      {result.primaryAffiliationName ? <div>{result.primaryAffiliationName}</div> : null}
      {locationAddress ? <div>{locationAddress}</div> : null}
    </div>
  );
}

export function SelectEntities({
  disabled,
  formatOptionLabel,
  onKeyDown,
  onInputFocus,
  onInputBlur,
  onInputChange,
  onSelection,
  placeholder,
  inputValue = '',
  isClearable,
  value,
  validEntities,
  queryFn,
}: SelectEntitiesProps) {
  const brandIdentifier = useActiveBrandIdentifier();
  const territoryId = useSelectedUserTerritoryId();

  const searchQuery = useQuery({
    queryKey: ['get:smart-routing-entities-search', inputValue, brandIdentifier, territoryId, validEntities],
    queryFn: () => {
      if (!brandIdentifier) {
        return Promise.resolve({ results: [] as SmartRoutingSearchEntity[] });
      }

      return queryFn({
        name: inputValue,
        brandIdentifier,
        territoryId,
        validEntities,
      });
    },
    enabled: inputValue.length > 2 && !disabled && Boolean(brandIdentifier),
    placeholderData: { results: [] },
  });

  const queryResults = searchQuery.data?.results ?? [];

  const theme = useTheme();
  const selectStyles = useMemo(
    () =>
      buildSelectStyles(theme) as StylesConfig<
        SmartRoutingSearchEntity,
        false,
        GroupBase<SmartRoutingSearchEntity>
      >,
    [theme]
  );
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  const resolvedFormatOptionLabel = (
    data: SmartRoutingSearchEntity,
    formatMeta: FormatOptionLabelMeta<SmartRoutingSearchEntity>
  ): ReactNode => {
    if (formatOptionLabel) {
      return formatOptionLabel(data, formatMeta);
    }

    return defaultFormatOptionLabel(data);
  };

  return (
    <SelectAsync<SmartRoutingSearchEntity>
      menuPortalTarget={document.body}
      isDisabled={disabled}
      isClearable={isClearable}
      onKeyDown={onKeyDown}
      onFocus={onInputFocus}
      onBlur={onInputBlur}
      onChange={(option) => {
        onSelection(option ?? null);
      }}
      inputValue={inputValue}
      value={value}
      isLoading={searchQuery.isFetching}
      options={queryResults}
      getOptionLabel={defaultGetOptionLabel}
      getOptionValue={defaultGetOptionValue}
      formatOptionLabel={resolvedFormatOptionLabel}
      noOptionsMessage={() => null}
      onInputChange={(value, meta) => (meta.action === 'input-change' ? onInputChange(value) : null)}
      placeholder={placeholder}
      styles={selectStyles}
      filterOption={() => true}
      theme={selectTheme}
    />
  );
}
